import React, { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { userService } from '../../_services';
import PasswordStrengthMeter, {
    isPasswordStrong,
    PASSWORD_NOT_STRONG_ENOUGH,
    passwordHasDisallowedWhitespace,
    PASSWORD_SPACE_NOT_ALLOWED,
} from '@/components/ui/PasswordStrengthMeter';
import { buildPasswordPolicyContext, getPasswordPolicyViolationMessage } from '@/_helpers/passwordPolicy';

function ChangePassword({
    titleText,
    userEmail, 
    userName, 
    dataId, 
    wholesalerName, 
    wholesalerId,
    activeManageEmailPagin,
    closeResetPasswordPopup, 
    openManageEmailPopup 
}) {
    const [newpwd, setNewpwd] = useState('');
    const [confirmpwd, setConfirmpwd] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [newpwdError, setNewpwdError] = useState(false);
    const [confirmpwdError, setConfirmpwdError] = useState(false);
    const [showNewpwd, setShowNewpwd] = useState(false);
    const [showConfirmpwd, setShowConfirmpwd] = useState(false);

    const policyContext = useMemo(
        () => buildPasswordPolicyContext({ email: userEmail, username: userName, fullName: userName || '' }),
        [userEmail, userName]
    );

    const handleResponse = (response) => {
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            return data;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'newpwd') {
            setNewpwd(value);
            setNewpwdError(false);
        } else {
            setConfirmpwd(value);
            setConfirmpwdError(false);
        }
        setError('');
    };

    const resetPassword = useCallback(async (UserEmail, UserName, UserId, WholeSaler, NewPassword) => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ UserEmail, UserName, UserId, WholeSaler, NewPassword })
            };
            
            const response = await fetch(`/api/user/ResetWhAccountPwd`, requestOptions);
            const apidata = await handleResponse(response);
            
            if (apidata.length > 0) {
                if (apidata[0].status_code == "1") {
                    setSuccessMessage(apidata[0].status_message);
                    setError('');
                    setLoading(false);
                    setTimeout(() => {
                        cancelPopup();
                    }, 1500);
                } else {
                    setError('Whoops, something went wrong. Please try again');
                    setSuccessMessage('');
                    setLoading(false);
                }
            } else {
                setError('Whoops, something went wrong. Please try again');
                setSuccessMessage('');
                setLoading(false);
            }
        } catch (error) {
            setError('Whoops, something went wrong. Please try again');
            setLoading(false);
        }
    }, []);

    const cancelPopup = useCallback(() => {
        closeResetPasswordPopup();
        openManageEmailPopup(dataId, wholesalerId, wholesalerName, activeManageEmailPagin);
    }, [closeResetPasswordPopup, openManageEmailPopup, dataId, wholesalerId, wholesalerName, activeManageEmailPagin]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        setNewpwdError(false);
        setConfirmpwdError(false);

        let isErrorExists = false;

        if (!newpwd && !confirmpwd) {
            setNewpwdError(true);
            setConfirmpwdError(true);
            setError('New password and Confirm new password required');
            setLoading(false);
            isErrorExists = true;
        } else if (userService.ValidateSpace(newpwd)) {
            setError('Space not acceptable');
            setNewpwdError(true);
            setLoading(false);
            isErrorExists = true;
        } else if (passwordHasDisallowedWhitespace(newpwd)) {
            setError(PASSWORD_SPACE_NOT_ALLOWED);
            setNewpwdError(true);
            setLoading(false);
            isErrorExists = true;
        } else if (!newpwd) {
            setNewpwdError(true);
            setError('New password is required');
            setLoading(false);
            isErrorExists = true;
        } else if (newpwd.length < 8) {
            setError('Password must be at least 8 characters');
            setNewpwdError(true);
            setLoading(false);
            isErrorExists = true;
        } else if (!isPasswordStrong(newpwd, policyContext)) {
            setError(getPasswordPolicyViolationMessage(newpwd, policyContext) || PASSWORD_NOT_STRONG_ENOUGH);
            setNewpwdError(true);
            setLoading(false);
            isErrorExists = true;
        } else if (!confirmpwd) {
            setConfirmpwdError(true);
            setError('Confirm new password is required');
            setLoading(false);
            isErrorExists = true;
        } else if (newpwd !== confirmpwd) {
            setError('New Password and Confirm Password must match');
            setConfirmpwdError(true);
            setLoading(false);
            isErrorExists = true;
        }

        if (isErrorExists) {
            return;
        }

        resetPassword(userEmail, userName, dataId, wholesalerName, newpwd);
    };

    return (
        <Dialog open={true} onOpenChange={cancelPopup}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{titleText || 'Reset Password'}</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="newpwd">New Password</Label>
                        <div className="relative">
                            <Input 
                                type={showNewpwd ? 'text' : 'password'}
                                id="newpwd"
                                name="newpwd"
                                value={newpwd}
                                onChange={handleChange}
                                autoComplete="off"
                                maxLength={16}
                                minLength={8}
                                className={`pr-10${newpwdError ? ' border-destructive' : ''}`}
                            />
                            <button type="button" tabIndex={-1} onClick={() => setShowNewpwd(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showNewpwd ? 'Hide password' : 'Show password'}>
                                {showNewpwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        <PasswordStrengthMeter password={newpwd} policyContext={policyContext} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmpwd">Confirm New Password</Label>
                        <div className="relative">
                            <Input 
                                type={showConfirmpwd ? 'text' : 'password'}
                                id="confirmpwd"
                                name="confirmpwd"
                                value={confirmpwd}
                                onChange={handleChange}
                                autoComplete="off"
                                maxLength={16}
                                minLength={8}
                                className={`pr-10${confirmpwdError ? ' border-destructive' : ''}`}
                            />
                            <button type="button" tabIndex={-1} onClick={() => setShowConfirmpwd(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showConfirmpwd ? 'Hide password' : 'Show password'}>
                                {showConfirmpwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {successMessage && (
                        <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm border border-green-200">
                            {successMessage}
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-destructive mt-2">{error}</div>
                    )}

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            type="submit" 
                            disabled={loading}
                            className="bg-primary hover:bg-primary/90"
                        >
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Submit
                        </Button>
                        <Button 
                            type="button" 
                            variant="secondary" 
                            onClick={cancelPopup}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ChangePassword;
